/**
 * Composición corporal (§5.3) — el bloque central de la app.
 *
 * El punto de partida estadístico, que condiciona todo lo demás: **las balanzas de
 * bioimpedancia estiman, no miden**. El % de grasa se mueve 2–4 puntos entre la mañana y la
 * noche sin que cambie un gramo de tejido, y frente a un DEXA la desviación ronda 3–5
 * puntos. Por eso acá no existe ninguna función que devuelva "el % de grasa de hoy" como si
 * fuera un hecho: la serie principal es siempre la media móvil, y todo cálculo lleva
 * consigo cuántas lecturas lo respaldan.
 */

import { addDias, aTimestamp, enVentana, ventana } from '../dates.ts';
import type { Condiciones, Fecha, MedidaCorporal, Sexo } from '../types.ts';
import { enUnidad } from '../units.ts';

export const AVISO_BIOIMPEDANCIA =
  'Las balanzas de bioimpedancia estiman, no miden. Una lectura suelta se mueve con la ' +
  'hidratación, la comida y la hora del día. Mirá la tendencia, no el punto.';

export const AVISO_CONDICIONES =
  'Estás comparando lecturas tomadas en condiciones distintas (ayuno vs. post-entreno). ' +
  'La diferencia puede ser del método de medición, no de tu cuerpo.';

export const CONSEJO_MEDICION =
  'Medite siempre en la misma ventana horaria, en ayunas y después de ir al baño. La ' +
  'consistencia importa más que la exactitud.';

/** §5.3 — ninguna conclusión de composición corporal con menos de esto. */
export const MIN_LECTURAS_COMPARABLES = 3;

// ---------------------------------------------------------------------------
// Media móvil
// ---------------------------------------------------------------------------

export interface PuntoSerie {
  fecha: Fecha;
  valor: number;
}

export interface PuntoConMedia {
  fecha: Fecha;
  /** El punto crudo. Va en gris de fondo en la UI, nunca como serie principal. */
  valor: number;
  /** Media móvil hasta esta fecha. Es la serie principal. */
  media: number;
  /**
   * Cuántas lecturas entraron en la media. Con n=1 la "media" es un punto suelto
   * disfrazado: la UI tiene que poder decirlo en vez de fingir suavizado.
   */
  n: number;
}

/**
 * Media móvil de ventana móvil hacia atrás: para cada lectura, promedia todas las lecturas
 * de los `dias` días que terminan en su fecha (ambos extremos inclusive).
 */
export function mediaMovil(puntos: PuntoSerie[], dias = 7): PuntoConMedia[] {
  const orden = [...puntos].sort((a, b) => aTimestamp(a.fecha) - aTimestamp(b.fecha));
  return orden.map((p) => {
    const desde = addDias(p.fecha, -(dias - 1));
    let suma = 0;
    let n = 0;
    for (const q of orden) {
      if (q.fecha >= desde && q.fecha <= p.fecha) {
        suma += q.valor;
        n++;
      }
      if (q.fecha > p.fecha) break;
    }
    return { fecha: p.fecha, valor: p.valor, media: suma / n, n };
  });
}

/** Último valor de la media móvil en o antes de `fecha`. */
export function mediaEn(serie: PuntoConMedia[], fecha: Fecha): PuntoConMedia | null {
  let out: PuntoConMedia | null = null;
  for (const p of serie) {
    if (p.fecha <= fecha) out = p;
    else break;
  }
  return out;
}

/** Primer punto de la media móvil en o después de `fecha`. */
export function mediaDesde(serie: PuntoConMedia[], fecha: Fecha): PuntoConMedia | null {
  for (const p of serie) if (p.fecha >= fecha) return p;
  return null;
}

// ---------------------------------------------------------------------------
// Derivación de una medida
// ---------------------------------------------------------------------------

export interface MedidaDerivada {
  medida: MedidaCorporal;
  fecha: Fecha;
  pesoKg: number;
  grasaKg: number | null;
  magraKg: number | null;
  /** §5.3 — masa magra / altura². Requiere altura. */
  ffmi: number | null;
  /** Índice de masa grasa: masa grasa / altura². */
  fmi: number | null;
  condiciones: Condiciones;
}

/**
 * Las masas de la balanza (magra, muscular, ósea) se registran en la misma unidad que el
 * peso. Se convierte a kg sólo para calcular; el registro guarda lo que el usuario leyó.
 */
export function derivar(m: MedidaCorporal, alturaCm: number | null): MedidaDerivada {
  const u = m.unidad_peso;
  const pesoKg = enUnidad({ valor: m.peso, unidad: u }, 'kg');

  const magraDeclarada = m.masa_magra === null ? null : enUnidad({ valor: m.masa_magra, unidad: u }, 'kg');
  const grasaDePct = m.grasa_pct === null ? null : (pesoKg * m.grasa_pct) / 100;

  const grasaKg = grasaDePct ?? (magraDeclarada === null ? null : pesoKg - magraDeclarada);
  const magraKg = magraDeclarada ?? (grasaKg === null ? null : pesoKg - grasaKg);

  const alturaM = alturaCm && alturaCm > 0 ? alturaCm / 100 : null;
  const alturaM2 = alturaM ? alturaM * alturaM : null;

  return {
    medida: m,
    fecha: m.fecha,
    pesoKg,
    grasaKg,
    magraKg,
    ffmi: alturaM2 && magraKg !== null ? magraKg / alturaM2 : null,
    fmi: alturaM2 && grasaKg !== null ? grasaKg / alturaM2 : null,
    condiciones: m.condiciones,
  };
}

export function ffmi(masaMagraKg: number, alturaCm: number): number | null {
  if (!(masaMagraKg > 0) || !(alturaCm > 0)) return null;
  const m = alturaCm / 100;
  return masaMagraKg / (m * m);
}

export function fmi(masaGrasaKg: number, alturaCm: number): number | null {
  if (!(masaGrasaKg >= 0) || !(alturaCm > 0)) return null;
  const m = alturaCm / 100;
  return masaGrasaKg / (m * m);
}

/**
 * Altura en cm despejada del IMC que reporta la balanza: IMC = kg / m².
 *
 * El FFMI necesita altura y el onboarding la pide, pero si el usuario no la sabe de memoria
 * y su balanza ya muestra IMC, el dato está ahí: con 97,5 kg y un IMC de 30,5 la altura es
 * 179 cm. Se ofrece como valor a confirmar, nunca se guarda solo — el IMC viene redondeado a
 * un decimal, así que el resultado tiene un margen de ±1 cm.
 */
export function alturaDesdeIMC(pesoKg: number, imc: number): number | null {
  if (!(pesoKg > 0) || !(imc > 0)) return null;
  const metros = Math.sqrt(pesoKg / imc);
  if (!Number.isFinite(metros) || metros < 1 || metros > 2.5) return null;
  return Math.round(metros * 100);
}

/**
 * Bandas de referencia de FFMI. La fuente de la spec sólo da rangos para hombres; para
 * cualquier otro caso se devuelve null en vez de inventar una banda.
 */
export const RANGOS_FFMI_HOMBRE = [
  { max: 19, etiqueta: 'Sin entrenamiento de fuerza', rango: '18–19' },
  { max: 21, etiqueta: 'Constitución atlética', rango: '20–21' },
  { max: 23, etiqueta: 'Muy musculado', rango: '22–23' },
  { max: Infinity, etiqueta: 'Excepcional', rango: '24+' },
] as const;

export function bandaFFMI(valor: number, sexo: Sexo): { etiqueta: string; rango: string } | null {
  if (sexo !== 'M') return null;
  for (const b of RANGOS_FFMI_HOMBRE) if (valor <= b.max) return { etiqueta: b.etiqueta, rango: b.rango };
  return null;
}

// ---------------------------------------------------------------------------
// Series de composición
// ---------------------------------------------------------------------------

export interface SeriesComposicion {
  derivadas: MedidaDerivada[];
  peso: PuntoConMedia[];
  grasaKg: PuntoConMedia[];
  magraKg: PuntoConMedia[];
  grasaPct: PuntoConMedia[];
  ffmi: PuntoConMedia[];
}

export function seriesComposicion(
  medidas: MedidaCorporal[],
  alturaCm: number | null,
  diasMedia = 7,
): SeriesComposicion {
  const derivadas = medidas
    .map((m) => derivar(m, alturaCm))
    .sort((a, b) => aTimestamp(a.fecha) - aTimestamp(b.fecha));

  const serieDe = (sel: (d: MedidaDerivada) => number | null): PuntoConMedia[] =>
    mediaMovil(
      derivadas
        .map((d) => ({ fecha: d.fecha, valor: sel(d) }))
        .filter((p): p is PuntoSerie => p.valor !== null),
      diasMedia,
    );

  return {
    derivadas,
    peso: serieDe((d) => d.pesoKg),
    grasaKg: serieDe((d) => d.grasaKg),
    magraKg: serieDe((d) => d.magraKg),
    grasaPct: serieDe((d) => d.medida.grasa_pct),
    ffmi: serieDe((d) => d.ffmi),
  };
}

// ---------------------------------------------------------------------------
// Condiciones de medición
// ---------------------------------------------------------------------------

export function agruparPorCondiciones(medidas: MedidaCorporal[]): Map<Condiciones, MedidaCorporal[]> {
  const out = new Map<Condiciones, MedidaCorporal[]>();
  for (const m of medidas) {
    const arr = out.get(m.condiciones);
    if (arr) arr.push(m);
    else out.set(m.condiciones, [m]);
  }
  return out;
}

/** true si el conjunto mezcla ayuno con post-entreno: la comparación queda advertida. */
export function condicionesMixtas(medidas: MedidaCorporal[]): boolean {
  const set = new Set(medidas.map((m) => m.condiciones));
  set.delete('desconocido');
  return set.size > 1;
}

// ---------------------------------------------------------------------------
// KPI principal: calidad de la pérdida (p-ratio) — §5.3
// ---------------------------------------------------------------------------

export type EstadoPRatio =
  | 'ok'
  | 'insuficiente'
  | 'sin_perdida'
  | 'sin_datos_grasa';

export type InterpretacionPRatio = 'muy_buena' | 'aceptable' | 'baja';

export interface PRatio {
  estado: EstadoPRatio;
  /** (grasa perdida / peso total perdido) × 100. null si no se puede calcular. */
  calidad: number | null;
  interpretacion: InterpretacionPRatio | null;
  pesoPerdidoKg: number | null;
  grasaPerdidaKg: number | null;
  magraPerdidaKg: number | null;
  lecturas: number;
  desde: Fecha | null;
  hasta: Fecha | null;
  /** Calidad > 100: bajó grasa y además ganó masa magra. Es el mejor caso posible. */
  recomposicion: boolean;
  condicionesMixtas: boolean;
  /** Explicación en una línea, lista para mostrar. */
  detalle: string;
}

export const UMBRAL_PRATIO_BUENO = 80;
export const UMBRAL_PRATIO_BAJO = 60;

export interface OpcionesPRatio {
  ventanaDias?: number;
  hasta?: Fecha;
  minLecturas?: number;
  diasMedia?: number;
}

/**
 * §5.3 y criterio de aceptación 7: **se calcula sobre medias móviles, no sobre lecturas
 * sueltas**. Comparar la lectura del lunes contra la del lunes anterior mide sobre todo
 * hidratación.
 *
 * Devuelve un estado explícito en vez de un número engañoso cuando no hay base:
 * - `insuficiente`   → menos de 3 lecturas comparables (§5.3).
 * - `sin_perdida`    → el peso no bajó en la ventana; un cociente con divisor ≤ 0 no
 *                      significa nada, así que no se inventa uno.
 * - `sin_datos_grasa`→ hay peso pero no hay % de grasa ni masa magra.
 */
export function pRatio(
  medidas: MedidaCorporal[],
  alturaCm: number | null,
  opciones: OpcionesPRatio = {},
): PRatio {
  const { ventanaDias = 28, minLecturas = MIN_LECTURAS_COMPARABLES, diasMedia = 7 } = opciones;

  const ordenadas = [...medidas].sort((a, b) => aTimestamp(a.fecha) - aTimestamp(b.fecha));
  const hasta = opciones.hasta ?? ordenadas[ordenadas.length - 1]?.fecha ?? null;

  const base: PRatio = {
    estado: 'insuficiente',
    calidad: null,
    interpretacion: null,
    pesoPerdidoKg: null,
    grasaPerdidaKg: null,
    magraPerdidaKg: null,
    lecturas: 0,
    desde: null,
    hasta,
    recomposicion: false,
    condicionesMixtas: false,
    detalle: 'Todavía no hay lecturas suficientes.',
  };

  if (!hasta) return base;

  const v = ventana(hasta, ventanaDias);
  const enRango = ordenadas.filter((m) => enVentana(m.fecha, v));
  base.lecturas = enRango.length;
  base.condicionesMixtas = condicionesMixtas(enRango);

  if (enRango.length < minLecturas) {
    base.detalle = `Hacen falta ${minLecturas} lecturas en los últimos ${ventanaDias} días; hay ${enRango.length}.`;
    return base;
  }

  const s = seriesComposicion(enRango, alturaCm, diasMedia);
  const pesoIni = mediaDesde(s.peso, v.desde);
  const pesoFin = mediaEn(s.peso, v.hasta);
  const grasaIni = mediaDesde(s.grasaKg, v.desde);
  const grasaFin = mediaEn(s.grasaKg, v.hasta);

  if (!pesoIni || !pesoFin) {
    base.detalle = 'No hay lecturas de peso suficientes en la ventana.';
    return base;
  }

  const pesoPerdido = pesoIni.media - pesoFin.media;
  base.pesoPerdidoKg = pesoPerdido;
  base.desde = pesoIni.fecha;
  base.hasta = pesoFin.fecha;

  if (!grasaIni || !grasaFin) {
    base.estado = 'sin_datos_grasa';
    base.detalle =
      'Hay peso pero no hay % de grasa ni masa magra: sin eso no se puede saber qué parte de lo que bajaste era grasa.';
    return base;
  }

  const grasaPerdida = grasaIni.media - grasaFin.media;
  const magraIni = mediaDesde(s.magraKg, v.desde);
  const magraFin = mediaEn(s.magraKg, v.hasta);
  base.grasaPerdidaKg = grasaPerdida;
  base.magraPerdidaKg = magraIni && magraFin ? magraIni.media - magraFin.media : null;

  if (pesoPerdido <= 0.05) {
    base.estado = 'sin_perdida';
    base.detalle =
      pesoPerdido < -0.05
        ? `El peso subió ${(-pesoPerdido).toFixed(1)} kg en la ventana; la calidad de la pérdida no aplica.`
        : 'El peso se mantuvo estable en la ventana; la calidad de la pérdida no aplica.';
    return base;
  }

  const calidad = (grasaPerdida / pesoPerdido) * 100;
  const interpretacion: InterpretacionPRatio =
    calidad >= UMBRAL_PRATIO_BUENO ? 'muy_buena' : calidad >= UMBRAL_PRATIO_BAJO ? 'aceptable' : 'baja';

  return {
    ...base,
    estado: 'ok',
    calidad,
    interpretacion,
    recomposicion: calidad > 100,
    detalle:
      calidad > 100
        ? `Bajaste ${pesoPerdido.toFixed(1)} kg y perdiste ${grasaPerdida.toFixed(1)} kg de grasa: además ganaste masa magra.`
        : `De los ${pesoPerdido.toFixed(1)} kg que bajaste, ${grasaPerdida.toFixed(1)} kg fueron grasa.`,
  };
}

// ---------------------------------------------------------------------------
// Velocidad de pérdida (§5.3, §7)
// ---------------------------------------------------------------------------

/** §7 — por encima de esto sostenido, sube el riesgo de perder masa magra. */
export const LIMITE_PERDIDA_SEMANAL_PCT = 1.0;

export interface VelocidadPerdida {
  estado: 'ok' | 'insuficiente' | 'sin_perdida';
  kgPorSemana: number | null;
  porcentajeSemanal: number | null;
  semanas: number;
  excedeBanda: boolean;
  detalle: string;
}

export function velocidadPerdida(
  medidas: MedidaCorporal[],
  opciones: { ventanaDias?: number; hasta?: Fecha; minLecturas?: number; diasMedia?: number } = {},
): VelocidadPerdida {
  const { ventanaDias = 28, minLecturas = MIN_LECTURAS_COMPARABLES, diasMedia = 7 } = opciones;
  const ordenadas = [...medidas].sort((a, b) => aTimestamp(a.fecha) - aTimestamp(b.fecha));
  const hasta = opciones.hasta ?? ordenadas[ordenadas.length - 1]?.fecha;

  const vacio: VelocidadPerdida = {
    estado: 'insuficiente',
    kgPorSemana: null,
    porcentajeSemanal: null,
    semanas: 0,
    excedeBanda: false,
    detalle: 'Faltan lecturas para estimar la velocidad.',
  };
  if (!hasta) return vacio;

  const v = ventana(hasta, ventanaDias);
  const enRango = ordenadas.filter((m) => enVentana(m.fecha, v));
  if (enRango.length < minLecturas) return vacio;

  const serie = mediaMovil(
    enRango.map((m) => ({ fecha: m.fecha, valor: enUnidad({ valor: m.peso, unidad: m.unidad_peso }, 'kg') })),
    diasMedia,
  );
  const ini = serie[0];
  const fin = serie[serie.length - 1];
  if (!ini || !fin || ini.fecha === fin.fecha) return vacio;

  const dias = (aTimestamp(fin.fecha) - aTimestamp(ini.fecha)) / 86_400_000;
  const semanas = dias / 7;
  const perdidaKg = ini.media - fin.media;
  const kgPorSemana = perdidaKg / semanas;
  const porcentajeSemanal = (kgPorSemana / ini.media) * 100;

  if (perdidaKg <= 0) {
    return {
      estado: 'sin_perdida',
      kgPorSemana,
      porcentajeSemanal,
      semanas,
      excedeBanda: false,
      detalle: 'El peso no bajó en la ventana.',
    };
  }

  const excedeBanda = porcentajeSemanal > LIMITE_PERDIDA_SEMANAL_PCT;
  return {
    estado: 'ok',
    kgPorSemana,
    porcentajeSemanal,
    semanas,
    excedeBanda,
    detalle: excedeBanda
      ? `${porcentajeSemanal.toFixed(2)} %/semana está por encima de la banda recomendada de ${LIMITE_PERDIDA_SEMANAL_PCT} %.`
      : `${porcentajeSemanal.toFixed(2)} %/semana, dentro de la banda recomendada.`,
  };
}

// ---------------------------------------------------------------------------
// Tendencia de masa magra (§7)
// ---------------------------------------------------------------------------

export interface TendenciaMagra {
  enDescenso: boolean;
  lecturasSeguidas: number;
  valores: { fecha: Fecha; media: number }[];
  suficiente: boolean;
}

/**
 * §7 — masa magra en descenso `lecturas` veces seguidas. Se evalúa sobre la media móvil, no
 * sobre los puntos crudos: tres lecturas crudas bajando pueden ser tres días de menos agua.
 */
export function masaMagraEnDescenso(
  medidas: MedidaCorporal[],
  alturaCm: number | null,
  lecturas = MIN_LECTURAS_COMPARABLES,
  diasMedia = 7,
): TendenciaMagra {
  const s = seriesComposicion(medidas, alturaCm, diasMedia);
  const ultimas = s.magraKg.slice(-lecturas);
  const suficiente = ultimas.length >= lecturas;

  let enDescenso = suficiente;
  for (let i = 1; i < ultimas.length; i++) {
    if (ultimas[i]!.media >= ultimas[i - 1]!.media) {
      enDescenso = false;
      break;
    }
  }

  return {
    enDescenso,
    lecturasSeguidas: ultimas.length,
    valores: ultimas.map((p) => ({ fecha: p.fecha, media: p.media })),
    suficiente,
  };
}
